import {etatsDesPav, evenementsRetenus} from '../../selectors/etat.js';
import {filtrerPavs, patrimoinesRetenus} from '../../selectors/filtres.js';
import {kpi} from '../../selectors/kpi.js';
import {alertes} from '../../selectors/alertes.js';
import {repartitions} from '../../selectors/repartitions.js';
import {rseRetenue} from '../../selectors/rse.js';
import {etatParDefaut, toQuery} from '../../permalink.js';

// PIL-01 lot 5 : fonctions pures du Dashboard (R1 à R7). Aucun recalcul métier : tout vient des sélecteurs du lot 2.

const TIRET = '—';

// ---------------------------------------------------------------------------------------------------------------
// Formats

function nombreFr(n, decimales = 0) {
    return n.toLocaleString('fr-FR', {minimumFractionDigits: decimales, maximumFractionDigits: decimales});
}

/** Entier lisible (séparateur de milliers fr) ou « — ». */
export function formatNombre(n) {
    return n == null ? TIRET : nombreFr(n);
}

/** Kilogrammes -> tonnes à 1 décimale (« 0,3 t ») ou « — ». */
export function formatTonnes(kg) {
    return kg == null ? TIRET : `${nombreFr(kg / 1000, 1)} t`;
}

/** Ratio 0..1 -> « 78 % » (arrondi à l'entier) ou « — ». */
export function formatPct(ratio) {
    return ratio == null ? TIRET : `${Math.round(ratio * 100)} %`;
}

/** « 2026-10-19T09:33:10+00:00 » -> « 19/10/2026 » (lecture directe de la date du fichier, sans fuseau). */
export function dateFr(iso) {
    if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return TIRET;
    return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

/**
 * R5 : libellés des 4 tuiles RSE. `≈` devant chaque chiffre, `—` quand la valeur est absente (null en V1 pour
 * l'eau économisée). Eau : m³ (1 décimale) au-delà de 10 000 L, litres sinon.
 */
export function formatRse(rse) {
    const r = rse || {};
    const avecApprox = (valeur, unite) => (valeur == null ? TIRET : `${valeur} ${unite}`);
    const eau = r.eauLitres == null
        ? null
        : (r.eauLitres > 10000 ? [nombreFr(r.eauLitres / 1000, 1), 'm³'] : [nombreFr(Math.round(r.eauLitres)), 'L']);
    return {
        eauConsommee: eau ? avecApprox(eau[0], eau[1]) : TIRET,
        eauEconomisee: r.eauEconomisee == null ? TIRET : avecApprox(nombreFr(r.eauEconomisee / 1000, 1), 'm³'),
        km: avecApprox(r.km == null ? null : nombreFr(r.km), 'km'),
        kmParPav: avecApprox(r.kmParPavLave == null ? null : nombreFr(r.kmParPavLave, 1), 'km'),
        dechets: r.dechetsKg == null ? TIRET : `≈ ${nombreFr(r.dechetsKg / 1000, 1)} t`,
    };
}

// ---------------------------------------------------------------------------------------------------------------
// R1 : périodes

/** Dernière date entre deux dates ISO. */
const minDate = (a, b) => (a < b ? a : b);

/**
 * Périodes proposées : campagne 2026 par défaut (du 1er avril au min(31 octobre, aujourd'hui)), 2025, 2024, puis « Tout »
 * (première -> dernière donnée de `bornes`).
 */
export function periodes(aujourdhui, bornes) {
    const campagne = (annee, libelle) => ({
        id: String(annee),
        libelle,
        du: `${annee}-04-01`,
        au: minDate(`${annee}-10-31`, aujourdhui),
    });
    return [
        campagne(2026, 'Campagne 2026 (avril → octobre)'),
        campagne(2025, 'Campagne 2025'),
        campagne(2024, 'Campagne 2024'),
        {id: 'tout', libelle: 'Tout', du: (bornes && bornes.min) || '', au: (bornes && bornes.max) || aujourdhui},
    ];
}

// ---------------------------------------------------------------------------------------------------------------
// R2 : colonnes par acte

function plage(acte, {du, au}) {
    return {acte, du: du || '', au, at: au};
}

// Parc filtré par patrimoines : tableau mémoïsé par (index, patrimoines) pour que les caches d'`evenementsRetenus` /
// `rseRetenue` (clés sur l'identité du tableau) servent lors d'un changement de période.
const cachePavs = new WeakMap();
function pavsStables(index, etats, patrimoines) {
    let parCle = cachePavs.get(index);
    if (!parCle) cachePavs.set(index, (parCle = new Map()));
    const cle = patrimoines.join(',');
    let pavs = parCle.get(cle);
    if (!pavs) {
        pavs = filtrerPavs(index, etats, {patrimoines, commune: '', etats: [], element: ''});
        parCle.set(cle, pavs);
    }
    return pavs;
}

/** Sélection de PAV (parc actif des patrimoines cochés) et états lavage / maintenance / graffiti sur la période. */
export function selection(index, params) {
    const etatsLavage = etatsDesPav(index, plage('lavage', params));
    const etatsMaint = etatsDesPav(index, plage('maint', params));
    const etatsGraffiti = etatsDesPav(index, plage('graffiti', params));
    // Collecte simulée (lot 8) : seulement quand le bouton Collecte est actif.
    const etatsCollecte = index.collecte ? etatsDesPav(index, plage('collecte', params)) : null;
    const pavs = pavsStables(index, etatsLavage, params.patrimoines);
    return {pavs, etatsLavage, etatsMaint, etatsGraffiti, etatsCollecte};
}

function colonnes(pavs, {etatsLavage, etatsMaint, etatsGraffiti, etatsCollecte}) {
    const kl = kpi(pavs, etatsLavage, 'lavage');
    const km = kpi(pavs, etatsMaint, 'maint');
    const kg = kpi(pavs, etatsGraffiti, 'graffiti');
    // Colonne Collecte : absente (undefined) quand le bouton est à 0. `nbCollectes` compte les déclassés.
    let collecte;
    if (etatsCollecte) {
        const kc = kpi(pavs, etatsCollecte, 'collecte');
        collecte = {
            tonnageKg: kc.tonnageKg,
            nbCollectes: kc.nbCollectes,
            nbDeclasses: kc.nbDeclasses,
            tauxDeclassement: kc.nbCollectes > 0 ? kc.nbDeclasses / kc.nbCollectes : null,
        };
    }
    return {
        collecte,
        lavage: {
            parc: kl.parc,
            laves: kl.ras + kl.enDefaut,
            tauxService: kl.tauxService,
            realises: kl.realises,
            planifies: kl.planifies, // null en V1 : « — »
            impossibles: kl.tentatives - kl.realises, // nb d'événements IMPOSSIBLE retenus
        },
        maintenance: {
            parc: km.parc,
            inspectes: km.inspectes,
            couverture: km.couverture,
            ras: km.ras,
            enDefaut: km.enDefaut,
        },
        graffitis: {
            constates: kg.enDefaut, // nb de PAV `pb` pour l'acte graffiti
            remisEnEtat: null, // non raccordé en V1
        },
    };
}

/**
 * R2 : les trois colonnes du dashboard.
 * @param params {patrimoines: jetons (`intra` / `extra` / ids), du, au}
 */
export function colonnesActes(index, params) {
    const s = selection(index, params);
    return colonnes(s.pavs, s);
}

// ---------------------------------------------------------------------------------------------------------------
// R3 : alertes à date

/**
 * Descripteurs des 4 alertes (indépendantes de la période). `filtre` = surcharge de l'état de vue pour le lien carte
 * (`null` = tuile non cliquable, valeur non raccordée).
 */
export function tuilesAlertes(index, aujourdhui, {pavs = null} = {}) {
    const a = alertes(index, aujourdhui, {pavs});
    const etatsMaint = etatsDesPav(index, plage('maint', {du: '', au: aujourdhui}));
    const actifs = (pavs || index.pavs).filter((p) => p.statut !== 'RETIRÉ');
    const inspectes = actifs.filter((p) => etatsMaint.get(p.id).verdict !== 'ni').length;
    return [
        {
            id: 'impossiblesConsecutifs',
            titre: 'Lavage impossible 2 fois de suite',
            nb: a.impossiblesConsecutifs.nb,
            sousLigne: `motif n°1 : ${a.impossiblesConsecutifs.motifN1 ? a.impossiblesConsecutifs.motifN1.libelle : TIRET} · ${nomenclatures(index, a.impossiblesConsecutifs.pavIds)}`,
            filtre: {acte: 'lavage', du: '', au: aujourdhui, etats: ['imp']},
        },
        {
            id: 'enDefaut',
            titre: 'En défaut maintenance',
            nb: a.enDefautMaintenance.nb,
            sousLigne: `${inspectes > 0 ? formatPct(a.enDefautMaintenance.nb / inspectes) : TIRET} des inspectés · n°1 : ${a.enDefautMaintenance.elementN1 ? a.enDefautMaintenance.elementN1.libelle : TIRET}`,
            filtre: {acte: 'maint', du: '', au: aujourdhui, etats: ['pb']},
        },
        {
            id: 'signalements',
            titre: 'Signalements ouverts',
            nb: a.signalementsOuverts == null ? null : a.signalementsOuverts.nb,
            sousLigne: 'non raccordé',
            filtre: null,
        },
        {
            id: 'condamnees',
            titre: 'Bornes condamnées',
            nb: a.condamnees.nb,
            sousLigne: 'dernier passage : lavage impossible',
            filtre: {acte: 'lavage', du: '', au: aujourdhui, etats: ['imp']},
        },
    ];
}

/** 5 premières nomenclatures (tri naturel), « … » si davantage ; « — » si aucune. */
function nomenclatures(index, ids) {
    const liste = ids.map((id) => index.pavById.get(id)).filter(Boolean).map((p) => p.nomenclature).sort((x, y) => x.localeCompare(y, 'fr', {numeric: true}));
    return liste.length === 0 ? TIRET : `${liste.slice(0, 5).join(', ')}${liste.length > 5 ? '…' : ''}`;
}

// ---------------------------------------------------------------------------------------------------------------
// R4 : répartitions

/** Top 5 des communes en défaut (maintenance) + tableau par patrimoine (service lavage, couverture maintenance). */
export function repartitionsDashboard(index, pavs, {etatsLavage, etatsMaint}) {
    const rl = repartitions(pavs, etatsLavage, index);
    const rm = repartitions(pavs, etatsMaint, index);
    const maint = new Map(rm.parPatrimoine.map((l) => [l.patrimoineId, l]));
    const lignes = rl.parPatrimoine.map((l) => {
        const m = maint.get(l.patrimoineId);
        return {
            patrimoineId: l.patrimoineId,
            libelle: l.libelle,
            groupe: l.groupe,
            pav: l.pav,
            service: l.service,
            couverture: m ? m.couverture : null,
            enDefaut: m ? m.enDefaut : null,
            impossibles: l.impossibles,
        };
    });
    return {communes: rm.defautsParCommune, lignes, elementN1: rm.defautsParElement.length > 0 ? rm.defautsParElement[0] : null};
}

/** Largeur (0..100) de la barre d'une commune, proportionnelle au maximum de la liste. */
export function largeurBarre(nb, communes) {
    const max = communes.reduce((m, c) => Math.max(m, c.nb), 0);
    return max > 0 ? Math.round((nb / max) * 100) : 0;
}

// ---------------------------------------------------------------------------------------------------------------
// Calcul complet de la page (une seule passe, à mettre dans un useMemo)

/**
 * @param params {patrimoines: jetons, du, au}
 * @returns la sélection, les colonnes, les répartitions, la RSE et `vide` (R6 : parc > 0 mais aucun événement retenu).
 */
export function calculerDashboard(index, params) {
    const s = selection(index, params);
    const cols = colonnes(s.pavs, s);
    const retenus = evenementsRetenus(index, s.pavs, {du: params.du || '', au: params.au, at: params.au});
    return {
        ...s,
        colonnes: cols,
        repartitions: repartitionsDashboard(index, s.pavs, s),
        rse: rseRetenue(index, s.pavs, {du: params.du || '', au: params.au, at: params.au}),
        dechetsKg: cols.collecte ? cols.collecte.tonnageKg : null, // RSE « Déchets collectés » (bouton Collecte actif)
        vide: s.pavs.length > 0 && retenus.length === 0,
    };
}

// ---------------------------------------------------------------------------------------------------------------
// Liens vers la carte filtrée

/**
 * Query string (sans « ? ») de la carte pour une surcharge de l'état de vue. Base = état par défaut de la vue ;
 * patrimoines = ceux des jetons cochés ; période = celle du dashboard sauf surcharge.
 */
export function requeteCarte(index, aujourdhui, params, surcharge = {}) {
    const base = etatParDefaut(index, aujourdhui);
    const ids = [...patrimoinesRetenus(index, params.patrimoines)].sort((a, b) => a - b);
    return toQuery({
        ...base,
        patrimoines: ids,
        du: params.du || '',
        au: params.au,
        at: params.au,
        ...surcharge,
        ...(surcharge.au ? {at: surcharge.au} : {}),
    }, index);
}
